import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { PrimeArtifact } from '../../../types/primeArtifact';
import { ArtifactRelationship } from '../../../types/artifactRelationship';
import './HierarchicalView.css';

interface HierarchicalViewProps {
  artifacts: PrimeArtifact[];
  relationships: ArtifactRelationship[];
  width?: number;
  height?: number;
  onNodeClick?: (artifact: PrimeArtifact) => void;
}

// Custom node type that extends D3's hierarchy node
type CustomNode = d3.HierarchyNode<PrimeArtifact> & {
  id: string;
  name: string;
};

export const HierarchicalView: React.FC<HierarchicalViewProps> = ({
  artifacts,
  relationships,
  width = 800,
  height = 600,
  onNodeClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !artifacts.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const buildTree = (): d3.HierarchyNode<PrimeArtifact> => {
      const artifactMap = new Map<string, CustomNode>();

      // Create nodes for all artifacts
      artifacts.forEach((artifact) => {
        const node = d3.hierarchy(artifact) as CustomNode;
        node.id = artifact.id;
        node.name = artifact.name;
        artifactMap.set(artifact.id, node);
      });

      // Build relationships
      relationships.forEach((rel) => {
        const sourceNode = artifactMap.get(rel.source_artifact_id);
        const targetNode = artifactMap.get(rel.target_artifact_id);

        if (sourceNode && targetNode && rel.direction === 'outgoing') {
          if (!sourceNode.children) {
            sourceNode.children = [];
          }
          sourceNode.children.push(targetNode);
          targetNode.parent = sourceNode;
        }
      });

      // Find root nodes (nodes without parents)
      const rootNodes = Array.from(artifactMap.values()).filter((node) => !node.parent);

      // If multiple roots, create a virtual root
      if (rootNodes.length > 1) {
        const virtualRoot = d3.hierarchy({} as PrimeArtifact) as CustomNode;
        virtualRoot.id = 'root';
        virtualRoot.name = 'Root';
        virtualRoot.children = rootNodes;
        rootNodes.forEach((node) => (node.parent = virtualRoot));
        return virtualRoot;
      }

      return rootNodes[0] || d3.hierarchy({} as PrimeArtifact);
    };

    const root = buildTree();
    const treeLayout = d3.tree<PrimeArtifact>().size([height - 100, width - 200]);

    const treeData = treeLayout(root);

    const g = svg.append('g').attr('transform', `translate(100,50)`);

    // Add zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 2])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
      });

    svg.call(zoom);

    // Draw links
    const linkGenerator = d3
      .linkHorizontal<d3.HierarchyLink<PrimeArtifact>, d3.HierarchyPointNode<PrimeArtifact>>()
      .x((d) => d.y)
      .y((d) => d.x);

    g.selectAll('.link')
      .data(treeData.links())
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', linkGenerator)
      .style('fill', 'none')
      .style('stroke', '#ccc');

    // Create nodes
    const node = g
      .selectAll('.node')
      .data(treeData.descendants())
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d) => `translate(${d.y},${d.x})`);

    // Add circles to nodes
    node
      .append('circle')
      .attr('r', 5)
      .style('fill', (d) => getColorForType(d.data.artifact_type_id || 'default'));

    // Add labels to nodes
    node
      .append('text')
      .attr('dy', '.35em')
      .attr('x', (d) => (d.children ? -13 : 13))
      .style('text-anchor', (d) => (d.children ? 'end' : 'start'))
      .text((d) => d.data.name);

    // Add tooltips
    node.append('title').text((d) => d.data.description || d.data.name);

    // Add click handler
    if (onNodeClick) {
      node.on('click', (event, d) => {
        onNodeClick(d.data);
      });
    }
  }, [artifacts, relationships, width, height, onNodeClick]);

  return <svg ref={svgRef} width={width} height={height} style={{ overflow: 'hidden' }} />;
};

// Helper function to get color based on artifact type
const getColorForType = (type: string): string => {
  const colorMap: { [key: string]: string } = {
    process: '#4CAF50',
    resource: '#2196F3',
    material: '#FFC107',
    default: '#9E9E9E',
  };
  return colorMap[type] || colorMap.default;
};

export default HierarchicalView;
